import { useAsync } from "@react-hookz/web";
import { useEffect } from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useMyNews } from "scmp-app/lib/hooks";
import type { hooksMyDailyFiveQuery } from "scmp-app/queries/__generated__/hooksMyDailyFiveQuery.graphql";
import type { hooksUseMyDailyFiveContent$key } from "scmp-app/queries/__generated__/hooksUseMyDailyFiveContent.graphql";

export const useMyDailyFive = () => {
  const {
    getMyNewDailyFiveArticleIds: { recommendedArticleIds },
  } = useMyNews();
  const environment = useRelayEnvironment();

  const [{ result, status }, { execute }] = useAsync(async (myDailyFiveIds: string[]) => {
    try {
      const myDailyFiveContent = await fetchQuery<hooksMyDailyFiveQuery>(
        environment,
        graphql`
          query hooksMyDailyFiveQuery($entityIds: [String]!, $first: Int!) {
            contents(filter: { entityIds: $entityIds }, first: $first) {
              edges {
                node {
                  ...primaryArticleContent
                  ...secondaryArticleContent
                  ...tertiaryArticleContent
                  entityId
                }
              }
            }
          }
        `,
        {
          entityIds: recommendedArticleIds ?? [],
          first: recommendedArticleIds?.length ?? 0,
        },
        { fetchPolicy: "store-or-network" },
      ).toPromise();

      const articlesMap = Object.fromEntries(
        myDailyFiveContent?.contents?.edges?.map?.(edge => [edge.node.entityId, edge]) ?? [],
      );

      return myDailyFiveIds
        .filter(result => notEmpty(result) && notEmpty(articlesMap[result]))
        .map(result => articlesMap[result]);
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!recommendedArticleIds) return;
    void execute(recommendedArticleIds);
  }, [recommendedArticleIds, execute]);

  return { contents: result ?? [], status };
};

export const useMyDailyFiveArticle = (reference: hooksUseMyDailyFiveContent$key) => {
  const data = useFragment(
    graphql`
      fragment hooksUseMyDailyFiveContent on Content {
        ... on Article {
          paywallTypes {
            name
          }
        }
      }
    `,
    reference,
  );

  const isTrendingVisible = !!data?.paywallTypes?.find(type => type?.name === "Velocity");

  return isTrendingVisible;
};
